import { useEffect, useRef, useState } from 'react';

import { useIntersectionObserver } from '@common/hooks';

import { useVideoPlayer } from '../hooks/useVideoPlayer';
import { ShortClipModel } from '../utils/types';

export interface UseVideoServiceArgs {
  data: ShortClipModel;
  isActive: boolean;
}

export const useVideoService = ({ data, isActive }: UseVideoServiceArgs) => {
  const videoPlayer = useVideoPlayer();
  const videoRef = useRef<HTMLVideoElement>(null);

  const [viewVideoChange, setViewVideoChange] = useState(false);
  const [isPlayable, setIsPlayable] = useState(false);

  useIntersectionObserver(
    {
      target: videoRef,
      onIntersect: () => setViewVideoChange(true),
      threshold: 0.3,
    },
    [],
  );

  const playVideo = () => {
    setIsPlayable(false);

    try {
      videoPlayer.player?.currentTime(0);
      videoPlayer.player?.autoplay('muted');
    } catch (e) {
      console.error(e);
    }
  };

  const pauseVideo = () => {
    try {
      videoPlayer.player?.pause();
      videoPlayer.player?.currentTime(0);
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    if (!videoRef.current) {
      return;
    }

    videoPlayer.init(
      videoRef.current,
      {
        inactivityTimeout: 0,
        autoplay: 'muted',
        sources: [
          {
            src: data.video_url,
            type: 'application/x-mpegURL',
          },
          {
            src: data.video_url,
            type: 'application/vnd.apple.mpegurl',
          },
        ],
      },
      {
        onEnded: () => {
          pauseVideo();
          setIsPlayable(true);
        },
      },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videoRef.current]);

  useEffect(() => {
    const player = videoPlayer.player;

    return () => {
      if (player && !player.isDisposed()) {
        videoPlayer.destroy();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videoPlayer.player]);

  useEffect(() => {
    if (viewVideoChange && isActive) {
      playVideo();
    } else {
      pauseVideo();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewVideoChange, isActive]);

  return {
    videoRef,
    isPlayable,
    playVideo,
  };
};
